import { useEffect, useState } from 'react';
import { useReduxState } from 'hooks';
import { getMessageWithPlaceholders } from 'shared-locales';
import { getBlockNameByType } from 'lib/blocks';
import { getFormattedSize } from 'lib/common';
import { Alert } from 'components/common';
import { Box, BoxRow } from 'components/common/box';
import { CheckboxInput } from 'components/common/form/common';

import type {
  File,
  MappedFiles
} from 'components/courses/manage/ManageExportScorm';

interface Props {
  allFiles: File[];
  handleAllowDownload: React.Dispatch<React.SetStateAction<boolean>>;
  handleExcludeFile: (file: File) => void;
  handleIncludeFile: (file: File) => void;
  includedFiles: MappedFiles;
  overLimit: boolean;
}

const CourseDownloadScormManageIncludedFiles = ({
  allFiles,
  handleAllowDownload,
  handleExcludeFile,
  handleIncludeFile,
  includedFiles,
  overLimit = false
}: Props) => {
  const {
    messages,
    serverConfig: { courseScormExportFileSizeLimit }
  } = useReduxState();

  const [disabled, setDisabled] = useState(true);

  const handleChange = (file: File, checked: boolean) => {
    if (checked) handleExcludeFile(file);
    else handleIncludeFile(file);
  };

  const blockTypes = getBlockNameByType({ messages });

  const bytesOverLimit =
    (Object.keys(includedFiles || {}).reduce(
      (totalSize, fileId) => totalSize + includedFiles[fileId].size,
      0
    ) || 0) -
    courseScormExportFileSizeLimit * 1e6;

  useEffect(() => {
    if (disabled && bytesOverLimit <= 0) {
      setDisabled(false);
      handleAllowDownload(true);
    } else if (!disabled && bytesOverLimit > 0) {
      setDisabled(true);
      handleAllowDownload(false);
    }
  }, [bytesOverLimit]);

  return overLimit && allFiles.length ? (
    <Alert
      expand={true}
      text={
        <Box padding={0}>
          {allFiles.map((file) => {
            const filename = file.url.split('/').pop();
            const fileType = blockTypes[file.type];
            const fileSize = getFormattedSize(file.size);

            return (
              <BoxRow key={file.id}>
                <CheckboxInput
                  id={`file-option-${file.id}`}
                  label={`[${fileType}] ${filename} (${fileSize})`}
                  onChange={(ev) =>
                    handleChange(file, (ev.target as HTMLInputElement).checked)
                  }
                  {...{ checked: !Boolean(includedFiles[file.id]) }}
                />
              </BoxRow>
            );
          })}
        </Box>
      }
      title={getMessageWithPlaceholders({
        message: messages.manageExportScormIncludedFiles,
        placeholders: {
          size: bytesOverLimit > 0 ? getFormattedSize(bytesOverLimit) : 0
        }
      })}
      size="large"
      style={{ margin: '-5px 0 15px 0' }}
      type="error-light"
    />
  ) : null;
};

export default CourseDownloadScormManageIncludedFiles;
